import { Locale } from '__generated__/graphql';

// Ordered set of months.
export const months = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

const defaultLocale = {
  // https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
  dateFormats: {
    internal_date: 'yyyy-MM-dd',
    internal_time: 'HH:mm:ss',
    internal_date_time: 'yyyy-MM-dd HH:mm:ss',

    input_date: 'dd/MM/yyyy',
    input_time: 'HH:mm',
    input_date_time: 'dd/MM/yyyy HH:mm',

    display_date: 'MMMM d, yyyy',
    display_time: 'HH:mm',
    display_date_time: 'MMMM d, yyyy, HH:mm',

    display_date_without_year: 'MMMM d',
    display_date_time_without_year: 'MMMM d, HH:mm',

    display_compact_date: 'dd/MM/yyyy',
    display_compact_date_time: 'dd/MM/yyyy, HH:mm',

    display_month: 'MMMM yyyy',
    display_day_of_week: 'EEEE',
    display_time_slot_date_time: 'EEEE, MMMM d, HH:mm',
    display_time_slot_date: 'EEEE, MMMM d',
    display_month_only: 'MMMM',
    display_year_only: 'yyyy',

    date_of_birth: 'MMMM d, yyyy',

    timestamp: 't',
  },
  inputDateFormats: ['dd/MM/yyyy', 'dd-MM-yyyy', 'dd.MM.yyyy', 'ddMMyyyy'],
  inputTimeFormats: ['HH:mm', 'HHmm', 'HH'],
  thousandsSeparator: ',',
  decimalSeparator: '.',
};

const config = {
  baseUrl: window.location.origin, // Without trailing slash,
  backendPath: '/b',
  imageUploadPath: '/b/api/upload_image',
  locales: [
    Locale.de,
    Locale.en,
    Locale.es,
    Locale.fr,
    Locale.nl,
  ],
  supporta: {
    url: 'https://supporta.com',
    signUpUrl: {
      [Locale.de]: 'https://about.supporta.com/en/submit-charity',
      [Locale.en]: 'https://about.supporta.com/en/submit-charity',
      [Locale.es]: 'https://about.supporta.com/en/submit-charity',
      [Locale.nl]: 'https://about.supporta.com/nl/submit-charity',
      [Locale.fr]: 'https://about.supporta.com/en/submit-charity',
    },
  },
  resale: {
    infoUrl: {
      [Locale.de]: 'https://about.atleta.cc/en/features/ticket-resale',
      [Locale.en]: 'https://about.atleta.cc/en/features/ticket-resale',
      [Locale.es]: 'https://about.atleta.cc/en/features/ticket-resale',
      [Locale.fr]: 'https://about.atleta.cc/en/features/ticket-resale',
      [Locale.nl]: 'https://about.atleta.cc/features/ticket-resale',
    },
  },
  termsUrl: {
    [Locale.de]: 'https://about.atleta.cc/en/terms-and-conditions',
    [Locale.en]: 'https://about.atleta.cc/en/terms-and-conditions',
    [Locale.es]: 'https://about.atleta.cc/es/terms-and-conditions',
    [Locale.fr]: 'https://about.atleta.cc/en/terms-and-conditions',
    [Locale.nl]: 'https://about.atleta.cc/terms-and-conditions',
  },
  fallbackLocale: Locale.en,
  locale: {
    de: {
      ...defaultLocale,
      dateFormats: {
        ...defaultLocale.dateFormats,

        input_date: 'dd.MM.yyyy',
        input_time: 'HH:mm',
        input_date_time: 'dd.MM.yyyy HH:mm',

        display_date: 'd. MMMM yyyy',
        display_time: 'HH:mm',
        display_date_time: 'd. MMMM yyyy, HH:mm',

        display_date_without_year: 'd. MMMM',
        display_date_time_without_year: 'd. MMMM, HH:mm',

        display_compact_date: 'dd.MM.yyyy',
        display_compact_date_time: 'dd.MM.yyyy, HH:mm',

        display_month: 'MMMM yyyy',
        display_day_of_week: 'EEEE',
        display_time_slot_date_time: 'EEEE d. MMMM, HH:mm',
        display_time_slot_date: 'EEEE d. MMMM',
        display_month_only: 'MMMM',
        display_year_only: 'yyyy',

        date_of_birth: 'd. MMMM yyyy',
      },
      thousandsSeparator: '.',
      decimalSeparator: ',',
    },
    en: defaultLocale,
    es: {
      ...defaultLocale,
      dateFormats: {
        ...defaultLocale.dateFormats,

        input_date: 'dd-MM-yyyy',
        input_time: 'HH:mm',
        input_date_time: 'dd-MM-yyyy HH:mm',

        display_date: 'd MMMM yyyy',
        display_time: 'HH:mm',
        display_date_time: 'd MMMM yyyy, HH:mm',

        display_date_without_year: 'd MMMM',
        display_date_time_without_year: 'd MMMM, HH:mm',

        display_compact_date: 'dd-MM-yyyy',
        display_compact_date_time: 'dd-MM-yyyy, HH:mm',

        display_month: 'MMMM yyyy',
        display_day_of_week: 'EEEE',
        display_time_slot_date_time: 'EEEE d MMMM, HH:mm',
        display_time_slot_date: 'EEEE d MMMM',
        display_month_only: 'MMMM',
        display_year_only: 'yyyy',

        date_of_birth: 'd MMMM yyyy',
      },
      thousandsSeparator: '.',
      decimalSeparator: ',',
    },
    fr: {
      ...defaultLocale,
      dateFormats: {
        ...defaultLocale.dateFormats,

        input_date: 'dd-MM-yyyy',
        input_time: 'HH:mm',
        input_date_time: 'dd-MM-yyyy HH:mm',

        display_date: 'd MMMM yyyy',
        display_time: 'HH:mm',
        display_date_time: 'd MMMM yyyy, HH:mm',

        display_date_without_year: 'd MMMM',
        display_date_time_without_year: 'd MMMM, HH:mm',

        display_compact_date: 'dd-MM-yyyy',
        display_compact_date_time: 'dd-MM-yyyy, HH:mm',

        display_month: 'MMMM yyyy',
        display_day_of_week: 'EEEE',
        display_time_slot_date_time: 'EEEE d MMMM, HH:mm',
        display_time_slot_date: 'EEEE d MMMM',
        display_month_only: 'MMMM',
        display_year_only: 'yyyy',

        date_of_birth: 'd MMMM yyyy',
      },
      thousandsSeparator: '.',
      decimalSeparator: ',',
    },
    nl: {
      ...defaultLocale,
      dateFormats: {
        ...defaultLocale.dateFormats,

        input_date: 'dd-MM-yyyy',
        input_time: 'HH:mm',
        input_date_time: 'dd-MM-yyyy HH:mm',

        display_date: 'd MMMM yyyy',
        display_time: 'HH:mm',
        display_date_time: 'd MMMM yyyy, HH:mm',

        display_date_without_year: 'd MMMM',
        display_date_time_without_year: 'd MMMM, HH:mm',

        display_compact_date: 'dd-MM-yyyy',
        display_compact_date_time: 'dd-MM-yyyy, HH:mm',

        display_month: 'MMMM yyyy',
        display_day_of_week: 'EEEE',
        display_time_slot_date_time: 'EEEE d MMMM, HH:mm',
        display_time_slot_date: 'EEEE d MMMM',
        display_month_only: 'MMMM',
        display_year_only: 'yyyy',

        date_of_birth: 'd MMMM yyyy',
      },
      thousandsSeparator: '.',
      decimalSeparator: ',',
    },
  },

  googleMapsKey: 'AIzaSyACECP3ffAnnK5rjJTKKp_A4bGa9zoT1gc',
  rollbarAccessToken: 'fb21e4e4d1784c6783f949a1dee11f9a', // post_client_item
  googleAnalyticsId: 'G-JW3KBZ2D6P',
  hcaptchaSiteKey: '0fc8b188-108a-4e0c-b196-f34069c85312',
  loginEmailCodeLength: 5,
  authenticatorCodeLength: 6,
};

export type Config = typeof config;
export type LocaleConfig = typeof config.locale.en;
export type DateTimeFormat = keyof typeof config.locale.en.dateFormats;

export default config;
